'use client';

import {
	BigNum,
	BN,
	calculateWithdrawLimit,
	QUOTE_PRECISION_EXP,
	SpotBalanceType,
	SpotMarketConfig,
} from '@velocity-exchange/sdk';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import usePostHogCapture from 'src/hooks/posthog/usePostHogCapture';
import { useWithdrawCollateral } from 'src/hooks/driftActions/spot-management/useWithdrawCollateral';
import useAccountsBeingLiquidated from 'src/hooks/Liquidations/useAccountsBeingLiquidated';
import useAccountTargetSpotBalance, {
	AccountTargetSpotBalance,
} from 'src/hooks/useAccountTargetSpotBalance';
import useLazySubAccounts from 'src/hooks/useLazySubAccounts';
import useDriftAccountStore from 'src/stores/useDriftAccountsStore';
import { usePositionsByUserKey } from 'src/hooks/query/accountTradingData/usePositions';
import { useOrdersByUserKey } from 'src/hooks/query/accountTradingData/useOrders';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import useDriftClientIsReady from 'src/hooks/useDriftClientIsReady';
import useCollateralModalState from 'src/components/Modals/CollateralModal/hooks/useCollateralModalState';
import { CollateralModalProps } from 'src/components/Modals/CollateralModal/types';
import { MarketId, matchEnum, UIMarket } from '@velocity-exchange/common';
import NumLib from 'src/utils/NumLib';
import { isAmplifyAccountName } from 'src/utils/amplify';
import { useSyncLocalStorage } from 'src/hooks/useSyncLocalStorage';
import useGetOraclePriceForMarket from 'src/hooks/useGetOraclePriceForMarket';
import useAccountSpotBalances from 'src/hooks/useAccountBankBalances';
import Link from 'src/components/primitives/Link/Link';
import PresetTag from 'src/components/Tags/PresetTag';
import { CollateralInputV2 } from '../../Inputs/CollateralInput';
import { InputQuickActions } from '../../Inputs/InputQuickActions';
import Button from '../../primitives/Button/Button';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import { getCleanedMarketSymbol } from 'src/utils/market';
import { toNotional } from 'src/utils/formatting';
import useModal from 'src/components/Modals/hooks/useModal';
import useUnsettledPnl from 'src/components/Modals/CollateralModal/hooks/useUnsettledPnl';
import BaseModal from 'src/components/primitives/BaseModal/BaseModal';
import InlineAlert from 'src/components/primitives/InlineAlert/InlineAlert';
import SubAccountSelector from './components/SubAccountSelector';
import TextButton from 'src/components/primitives/TextButton/TextButton';
import useFeatureFlagStore from 'src/stores/featureFlag/useFeatureFlagStore';
import { fetchWithdrawalSupportedAssets } from 'src/hooks/useFunXyzActions';
import MarketIcon from 'src/components/Utils/MarketIcon';
import { ChevronRight } from '@velocity-exchange/icons';
import { cj } from 'src/utils/classNames';

const UnsettledAmountMessage = () => {
	const { hasUnsettled, unsettledNumber, handleSettleNow } = useUnsettledPnl();
	if (!hasUnsettled) return null;

	return (
		<div className="flex w-full items-start justify-start gap-xs typo-body-xs">
			<span className="text-text-secondary">
				You have an unsettled amount of {toNotional(unsettledNumber)}.
			</span>
			<TextButton onClick={handleSettleNow} variant="tertiary" size="sm">
				Settle now
			</TextButton>
		</div>
	);
};

const BalanceChanges = ({
	selectedCollatBank,
	collatTransferAmountBigNum,
	targetCollatBalances,
}: {
	selectedCollatBank: SpotMarketConfig;
	collatTransferAmountBigNum: BigNum;
	targetCollatBalances: AccountTargetSpotBalance;
}) => {
	const selectedCollatUISpotMarketConfig = UIMarket.createSpotMarket(
		selectedCollatBank.marketIndex
	);

	const balanceAfterTransfer = targetCollatBalances.netBaseBalance.sub(
		collatTransferAmountBigNum
	);

	return (
		<div className="flex flex-col gap-sm">
			<div className="flex items-center justify-between gap-xs">
				<span className="typo-body-xs text-text-secondary">
					New Asset Balance
				</span>
				<span className="typo-body-xs text-text-default">
					{`${balanceAfterTransfer.prettyPrint()} ${getCleanedMarketSymbol(
						selectedCollatUISpotMarketConfig.uiSymbols.baseAssetDisplaySymbol
					)}`}
				</span>
			</div>
		</div>
	);
};

const WithdrawalLimitDisplay = ({
	sizeButton,
}: {
	sizeButton: React.ReactNode;
}) => {
	return (
		<div className="flex items-center justify-between gap-xs">
			<span className="typo-body-xs text-text-secondary">
				Today&apos;s Withdrawal Limit
			</span>
			{sizeButton}
		</div>
	);
};

const WithdrawModal = ({
	collateralIndex,
	collateralPoolId,
	targetAccountKey,
	defaultLargestBalance,
}: CollateralModalProps) => {
	const { withdrawCollateral } = useWithdrawCollateral();
	const availableSubaccounts = useLazySubAccounts();
	const accountsBeingLiquidated = useAccountsBeingLiquidated();
	const { captureEvent } = usePostHogCapture();
	const isMobile = useIsMobileScreenSize();
	const { hide, show, push } = useModal();
	const driftClientIsReady = useDriftClientIsReady();
	const driftClient = useDriftStore((s) => s.driftClient.client);
	const isWithdrawToOtherDestinationsEnabled = useFeatureFlagStore((s) =>
		s.flagEnabled('ENABLE_DEPOSIT_FROM_OTHER_SOURCES')
	);

	// Prefetch withdrawal destination assets when feature is enabled
	useEffect(() => {
		if (!isWithdrawToOtherDestinationsEnabled) return;
		fetchWithdrawalSupportedAssets().catch(() => {});
	}, [isWithdrawToOtherDestinationsEnabled]);

	const hasCustomMaxMarginRatio = useDriftAccountStore(
		(s) =>
			!!s.accounts[s.currentUserKey ?? '']?.client?.getUserAccount()
				?.maxMarginRatio
	);

	const {
		updateCollateralIndex,
		handleTargetAccountChange,
		selectedCollateralMarket,
		selectedTargetAccountKey,
		selectedPoolId,
		defaultToLargestBalance,
	} = useCollateralModalState({
		collateralIndex,
		collateralPoolId,
		targetAccountKey,
		defaultLargestBalance,
	});

	const currentUserName = useDriftAccountStore(
		(s) => s.accounts[selectedTargetAccountKey ?? '']?.name ?? ''
	);

	const [_, setLastWithdrawAction] = useSyncLocalStorage<string | null>(
		'lastWithdrawAction',
		null
	);

	const [submitting, setSubmitting] = useState(false);
	const [collatTransferAmount, setCollatTransferAmount] = useState('');
	const [isMax, setIsMax] = useState(false);

	const getOraclePrice = useGetOraclePriceForMarket();

	const accountBalances = useAccountSpotBalances(
		selectedTargetAccountKey,
		null
	);
	const hasOpenBorrows = UIMarket.spotMarkets.reduce((hasBorrows, bank) => {
		if (hasBorrows) return true;

		const deposits = accountBalances.filter(
			(bal) =>
				bal.asset.marketIndex === bank.marketIndex &&
				matchEnum(bal.balanceType, SpotBalanceType.DEPOSIT)
		);
		const borrows = accountBalances.filter(
			(bal) =>
				bal.asset.marketIndex === bank.marketIndex &&
				matchEnum(bal.balanceType, SpotBalanceType.BORROW)
		);
		const depositsBase = NumLib.sumBigNums(
			deposits.map((depos) => depos.balance),
			bank.precisionExp
		);
		const borrowsBase = NumLib.sumBigNums(
			borrows.map((depos) => depos.balance),
			bank.precisionExp
		);

		const netBase = depositsBase.sub(borrowsBase);

		if (netBase.ltZero()) {
			return true;
		}

		return false;
	}, false) as boolean;

	const { data: positionsResult } = usePositionsByUserKey(
		selectedTargetAccountKey
	);
	const { data: ordersResult } = useOrdersByUserKey(selectedTargetAccountKey);
	const hasOpenPositions = !!positionsResult?.openPerpPositions?.length;
	const hasOpenOrders = !!ordersResult?.openOrders?.length;

	const selectedAccountCanBeLiqd = accountsBeingLiquidated.find(
		(account) => account.userKey === selectedTargetAccountKey
	);

	const targetCollatBalances = useAccountTargetSpotBalance(
		selectedCollateralMarket,
		selectedTargetAccountKey,
		true
	);

	// When isMax is true, use the full precision withdrawLimitBase to avoid dust from string truncation
	const collatTransferAmountBignum = isMax
		? targetCollatBalances.withdrawLimitBase
		: BigNum.fromPrint(
				collatTransferAmount ? collatTransferAmount : '0',
				selectedCollateralMarket.precisionExp
			);

	const withdrawalLimit = targetCollatBalances.withdrawLimitBase.shiftTo(
		selectedCollateralMarket.precisionExp
	);

	const exceededMax = collatTransferAmountBignum.gt(withdrawalLimit);

	const allowConfirmation =
		!submitting &&
		collatTransferAmount &&
		collatTransferAmountBignum.gt(
			BigNum.zero(selectedCollateralMarket.precisionExp),
			true
		) &&
		!selectedAccountCanBeLiqd &&
		!exceededMax;

	const showMarginWithdrawalLimitMessage =
		hasOpenPositions || hasOpenOrders || hasOpenBorrows;

	const showDailyWithdrawalLimitMessage = useMemo(() => {
		if (!withdrawalLimit.lt(targetCollatBalances.assetBaseBalance)) {
			return false;
		}

		if (!driftClientIsReady || !driftClient) {
			return false;
		}

		const spotMarket = driftClient.getSpotMarketAccount(
			selectedCollateralMarket.marketIndex
		);
		if (!spotMarket) {
			return false;
		}

		const nowTs = new BN(Math.floor(Date.now() / 1000));
		const { withdrawLimit: marketLimit } = calculateWithdrawLimit(
			spotMarket,
			nowTs
		);
		const marketWithdrawLimitBigNum = BigNum.from(
			marketLimit,
			selectedCollateralMarket.precisionExp
		);

		return (
			marketWithdrawLimitBigNum.lt(targetCollatBalances.assetBaseBalance) &&
			withdrawalLimit.lte(marketWithdrawLimitBigNum)
		);
	}, [
		withdrawalLimit,
		targetCollatBalances.assetBaseBalance,
		driftClientIsReady,
		driftClient,
		selectedCollateralMarket.marketIndex,
		selectedCollateralMarket.precisionExp,
	]);

	const showAmplifyWithdrawalMessage = isAmplifyAccountName(currentUserName);

	const targetUserAccount = useMemo(() => {
		return availableSubaccounts
			.find((acct) => acct.userKey === selectedTargetAccountKey)
			?.client?.getUserAccount();
	}, [availableSubaccounts, selectedTargetAccountKey]);

	const handleWithdraw = useCallback(async () => {
		setSubmitting(true);

		let result;
		try {
			const collateralAmount = BigNum.fromPrint(
				collatTransferAmount,
				selectedCollateralMarket.precisionExp
			);

			result = await withdrawCollateral({
				amount: collateralAmount,
				spotMarketConfig: selectedCollateralMarket,
				userAccount: targetUserAccount,
				isBorrow: false,
				isMax,
			});

			if (result === true) {
				captureEvent('collateral_withdrawal_submitted', {
					spot_market_symbol: selectedCollateralMarket.symbol,
					withdrawal_amount: collatTransferAmount,
				});

				setLastWithdrawAction(
					JSON.stringify({
						lastWithdrawTs: Date.now(),
						amount: collateralAmount.toNum(),
						market: selectedCollateralMarket.symbol,
						quoteAmount: collateralAmount
							.mul(
								getOraclePrice(
									MarketId.createSpotMarket(
										selectedCollateralMarket.marketIndex
									)
								) ?? BigNum.zero(QUOTE_PRECISION_EXP)
							)
							.shiftTo(QUOTE_PRECISION_EXP)
							.toNum(),
						accountKey: selectedTargetAccountKey,
					})
				);

				hide();
			}
		} finally {
			setSubmitting(false);
		}
	}, [
		collatTransferAmount,
		selectedCollateralMarket,
		selectedTargetAccountKey,
		targetUserAccount,
		isMax,
		captureEvent,
		setLastWithdrawAction,
		getOraclePrice,
		hide,
	]);

	const handleValueChange = (newValue?: string) => {
		if (newValue !== undefined) {
			setIsMax(false);

			// Set input to max amount if exceeds limit
			if (newValue === '') {
				setCollatTransferAmount(newValue);
				return;
			}

			const inputBigNum = BigNum.fromPrint(
				newValue,
				selectedCollateralMarket.precisionExp
			);
			if (inputBigNum.lte(targetCollatBalances.withdrawLimitBase)) {
				setCollatTransferAmount(newValue);
			} else {
				// Set to max amount if input exceeds limit
				setCollatTransferAmount(targetCollatBalances.withdrawLimitBase.print());
				setIsMax(true);
			}
		}
	};

	const handleMax = () => {
		setCollatTransferAmount(withdrawalLimit.toTradePrecision());
		setIsMax(true);
	};

	// If max is selected, keep input value in sync with max allowed value
	useEffect(() => {
		if (isMax) {
			setCollatTransferAmount(withdrawalLimit.toTradePrecision());
		}
	}, [isMax, withdrawalLimit.toTradePrecision()]);

	// Reset transfer amount if market selection changes
	useEffect(() => {
		setCollatTransferAmount('');
		setIsMax(false);
	}, [selectedCollateralMarket?.symbol, selectedTargetAccountKey]);

	// If the user has exceeded the max allowable withdrawal, set the withdrawal input amount to the max allowable withdrawal
	useEffect(() => {
		if (exceededMax && isMax) {
			setCollatTransferAmount(withdrawalLimit.toTradePrecision());
		}
	}, [exceededMax, isMax]);

	const sizePresets = (
		<InputQuickActions.SizePresets
			onClick={(amount) => {
				if (amount.eq(withdrawalLimit)) {
					handleMax();
				} else {
					setIsMax(false);
					setCollatTransferAmount(amount.print());
				}
			}}
			maxAmount={withdrawalLimit}
		/>
	);

	const showDesktopWithdrawToOtherDestinations =
		!isMobile && isWithdrawToOtherDestinationsEnabled;

	const handleWithdrawToOtherDestinations = () => {
		push({
			type: 'withdraw_to_other_destinations',
			props: {
				collateralIndex: selectedCollateralMarket.marketIndex,
				targetAccountKey: selectedTargetAccountKey,
			},
		});
	};

	const withdrawToOtherDestinationsContent = (
		<>
			<PresetTag preset="new" />

			<span className="typo-label-sm">Withdraw to other destinations</span>
			<div className="flex flex-1 items-center justify-end gap-sm">
				<div className="flex items-center">
					<MarketIcon marketSymbol="ETH" className="z-[1] size-lg" />
					<MarketIcon marketSymbol="BNB" className="z-[2] -ml-1 size-lg" />
					<MarketIcon marketSymbol="POL" className="z-[3] -ml-1 size-lg" />
					{!isMobile && (
						<>
							<MarketIcon marketSymbol="BASE" className="z-[4] -ml-1 size-lg" />
							<MarketIcon marketSymbol="ARB" className="z-[5] -ml-1 size-lg" />
						</>
					)}
					<div className="ml-px typo-body-xs">+</div>
				</div>
				<ChevronRight size={16} className="text-text-secondary" />
			</div>
		</>
	);

	return (
		<BaseModal.Container
			onClose={hide}
			className={cj(
				showDesktopWithdrawToOtherDestinations &&
					'overflow-visible bg-transparent shadow-none'
			)}
			dataTestId="withdraw-modal"
		>
			<div className="flex h-full flex-col overflow-hidden sm:rounded-lg sm:shadow-md">
				<BaseModal.Title onClose={hide}>Withdraw</BaseModal.Title>

				<BaseModal.Body>
					<SubAccountSelector
						mode="withdraw"
						selectedTargetAccountKey={selectedTargetAccountKey}
						selectedPoolId={selectedPoolId}
						handleTargetAccountChange={handleTargetAccountChange}
					/>

					<CollateralInputV2
						maxAmount={targetCollatBalances.withdrawLimitBase}
						selectedMarket={selectedCollateralMarket}
						onChangeValue={handleValueChange}
						onChangeMarket={(val) => updateCollateralIndex(val.marketIndex)}
						value={
							isMax
								? targetCollatBalances.withdrawLimitBase.toTradePrecision()
								: collatTransferAmount
						}
						label="Amount"
						defaultToHighestBalance={defaultToLargestBalance ?? false}
						source={{
							type: 'userAccount',
							userKey: selectedTargetAccountKey,
						}}
						customElementRight={!showDailyWithdrawalLimitMessage && sizePresets}
						poolId={selectedPoolId}
						collateralFilter={(balance) => {
							return balance.uiAmount > 0;
						}}
						fullScreenPopover={isMobile}
						fullScreenPopoverTitle="Select Asset"
					/>
					{showDailyWithdrawalLimitMessage && (
						<WithdrawalLimitDisplay sizeButton={sizePresets} />
					)}

					{showAmplifyWithdrawalMessage && (
						<InlineAlert
							variant="warning"
							title="Withdrawing will increase the leverage of your Amplify position. Please use the Amplify page unless you are doing this intentionally."
						/>
					)}
					{showMarginWithdrawalLimitMessage && (
						<InlineAlert
							variant="info"
							title="Your withdrawable amount may be lower due to open positions, orders, borrows, or limits."
						>
							<Link
								href="https://docs.drift.trade/trading/block-conditions#withdraws"
								openInNewTab
								variant="primary"
								className="typo-label-xs"
							>
								Learn More
							</Link>
						</InlineAlert>
					)}
					{showDailyWithdrawalLimitMessage && (
						<InlineAlert variant="warning" title="Limited withdrawal">
							To ensure there&apos;s enough liquidity, withdrawals are currently
							limited. You can withdraw up to {withdrawalLimit.prettyPrint()}{' '}
							{selectedCollateralMarket.symbol} currently. Please try again
							later.
						</InlineAlert>
					)}
					<BalanceChanges
						selectedCollatBank={selectedCollateralMarket}
						collatTransferAmountBigNum={collatTransferAmountBignum}
						targetCollatBalances={targetCollatBalances}
					/>

					<UnsettledAmountMessage />

					{selectedAccountCanBeLiqd && (
						<InlineAlert
							variant="error"
							title="Withdrawal is disabled during liquidation. Please try again later."
						/>
					)}

					{hasCustomMaxMarginRatio && (
						<InlineAlert
							variant="warning"
							title="Your custom max leverage settings may affect these amounts."
						>
							<TextButton
								onClick={() =>
									show({ type: 'settings', props: { view: 'margin' } })
								}
								variant="secondary"
								size="sm"
							>
								Click here to update
							</TextButton>
						</InlineAlert>
					)}
				</BaseModal.Body>
				<BaseModal.Footer>
					<Button
						className="w-full"
						size="lg"
						onClick={handleWithdraw}
						disabled={!allowConfirmation}
						isLoading={submitting}
						dataTestId="withdraw-modal-submit"
					>
						Withdraw
					</Button>
				</BaseModal.Footer>
			</div>

			{isWithdrawToOtherDestinationsEnabled && (
				<div
					className={cj(
						isMobile ? 'border-t border-stroke-divider p-lg' : 'pt-xs'
					)}
				>
					<button
						className={cj(
							'flex h-17 w-full items-center gap-x-sm rounded-sm px-lg text-text-default',
							isMobile
								? 'bg-button-secondary-bg hover:bg-button-secondary-bg-hover'
								: 'bg-container-bg shadow-md hover:bg-input-bg'
						)}
						onClick={handleWithdrawToOtherDestinations}
					>
						{withdrawToOtherDestinationsContent}
					</button>
				</div>
			)}
		</BaseModal.Container>
	);
};

export default React.memo(WithdrawModal);
